import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import { downloadBase64Pdf } from '@/utils/pdfDownload';
import type { CloseArTasksInput, ListArTasksInput } from '@erp/rpc';

/** AR Tasks page: metrics above the table. */
export function useArTasksSummary() {
  return useQuery(orpc.accountsReceivable.arTasks.summary.queryOptions({}));
}

/** AR Tasks page: one page of open billing tasks. */
export function useArTasksList(input: ListArTasksInput) {
  const query = useQuery(
    orpc.accountsReceivable.arTasks.list.queryOptions({ input }),
  );
  return {
    ...query,
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    assigneeGroups: query.data?.assigneeGroups,
  };
}

/**
 * AR task detail: the ERP's change log for the task's order, read live from
 * the ERP, so it is fetched once per task view rather than refetched on focus.
 */
export function useArTaskStatusHistory(taskId: string) {
  return useQuery(
    orpc.accountsReceivable.arTasks.statusHistory.queryOptions({
      input: { taskId },
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }),
  );
}

/**
 * Refetch the AR Tasks table and metrics plus the shared task caches (the
 * shared invalidation covers the AR Tasks keys, so task-view edits refresh
 * the page too).
 */
export function invalidateArTasksQueries(queryClient: QueryClient) {
  invalidateTaskQueries(queryClient);
}

export function useCloseArTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CloseArTasksInput) =>
      rpcClient.accountsReceivable.arTasks.close(input),
    onSuccess: () => invalidateArTasksQueries(queryClient),
  });
}

/**
 * Generates a PDF of the given tasks (mirroring the caller's visible column
 * layout) and triggers a browser download.
 */
export function useDownloadArTasksPdf() {
  return useMutation(
    orpc.accountsReceivable.arTasks.downloadPdf.mutationOptions({
      onSuccess: (data) => downloadBase64Pdf(data.pdf, data.filename),
    }),
  );
}
